import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'

export const ValidNewOwnersQuery = graphql(/* GraphQL */ `
  query ValidNewOwners {
    account {
      currentOrganization {
        ownerEmail
        validNewOwners {
          id
          name
          email
        }
      }
    }
  }
`)

export type ValidNewOwner = {
  id: string
  email: string
  name?: string | null
}

export type UseGetValidNewOrganizationOwnersResponse = {
  validNewOwners: ValidNewOwner[]
  currentOwnerEmail?: string
  isLoading: boolean
  hasError: boolean
}

/**
 * Custom hook to fetch valid new owners for the current organization.
 *
 * @returns {UseGetValidNewOrganizationOwnersResponse} The list of possible new owners and loading/error state.
 */
export const useGetValidNewOrganizationOwners =
  (): UseGetValidNewOrganizationOwnersResponse => {
    const { data, loading, error } = useQuery(ValidNewOwnersQuery)

    const validNewOwners = useMemo(
      () => data?.account?.currentOrganization?.validNewOwners ?? [],
      [data],
    )
    const currentOwnerEmail = data?.account?.currentOrganization?.ownerEmail

    // Prepend the current owner to the validNewOwners array if currentOwnerEmail exists
    const ownersWithCurrent = useMemo(() => {
      const isCurrentOwnerInList = validNewOwners.some((owner) => {
        return owner.email === currentOwnerEmail
      })
      return currentOwnerEmail && !isCurrentOwnerInList
        ? [
            {
              id: currentOwnerEmail,
              email: currentOwnerEmail,
              name: null,
            },
            ...validNewOwners,
          ]
        : validNewOwners
    }, [currentOwnerEmail, validNewOwners])

    if (error) {
      logError(error, { metaData: { query: 'ValidNewOwners' } })
    }

    return useMemo<UseGetValidNewOrganizationOwnersResponse>(
      () => ({
        validNewOwners: ownersWithCurrent,
        currentOwnerEmail,
        isLoading: loading,
        hasError: !!error,
      }),
      [ownersWithCurrent, currentOwnerEmail, loading, error],
    )
  }
